import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import { PASSWORD_MIN_LENGTH } from '@proctoring/shared';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Consumes the single-use token from a password-reset email and sets a new password.
 *
 * Mirrors SetupPasswordPage's structure and rules, with one deliberate difference: a
 * completed reset sends the user to /login to sign in with the password they just chose,
 * rather than resuming the session inline. Someone recovering an account has usually just
 * proved they are unsure of their credentials, and typing the new one once more is the
 * cheapest confirmation that it is what they think it is.
 */
export function ResetPasswordPage(): ReactElement {
  const [searchParams] = useSearchParams();
  const { resetPassword, logout } = useAuth();
  const navigate = useNavigate();

  const token = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-lg font-semibold text-session-text">Invalid reset link</h1>
          <p className="mt-2 text-sm text-session-muted">
            This link is missing its token. Open the link from your reset email again, or request a
            new one.
          </p>
          <Link to="/forgot-password" className="btn-secondary mt-5 inline-flex">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    // Both rules are re-checked server-side; validating here only avoids burning the
    // single-use token on a typo the user can still see and fix.
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirmation) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword({ token, password });
      // The API returns a session; drop it so the user lands on a clean sign-in.
      logout();
      navigate('/login', {
        replace: true,
        state: { notice: 'Your password has been reset. Sign in with your new password.' },
      });
    } catch (cause) {
      setError(messageForError(cause));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <h1 className="text-lg font-semibold tracking-tight text-session-text">
          Choose a new password
        </h1>
        <p className="mt-1 mb-6 text-sm text-session-muted">
          Choose a password of at least {PASSWORD_MIN_LENGTH} characters. This link can only be used
          once.
        </p>

        <form onSubmit={onSubmit} className="panel space-y-4 p-5" noValidate>
          {error ? (
            <p role="alert" className="form-error">
              {error}
            </p>
          ) : null}

          <div>
            <label htmlFor="password" className="field-label">
              New password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="confirmation" className="field-label">
              Confirm password
            </label>
            <input
              id="confirmation"
              name="confirmation"
              type="password"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              className="field-input"
            />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? 'Saving…' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  );
}
